import React from 'react';

import css from './EmptyState.module.css';

/**
 * EmptyState — canonical "nothing here yet" block.
 *
 * Handles the three most common empty-state shapes across the app:
 *   - no results (filters active)  → title + lead + "clear filters" text button
 *   - no data at all (zero state)  → title + lead + primary CTA
 *   - success / confirmation       → icon + title + lead (no action)
 *
 * This is the consolidated version of the inline EmptyState in
 * WheelplaceRequestGrid. Migrate that grid's EmptyState to use this
 * primitive once we're happy with the API.
 *
 * API:
 *   - icon:     React node (any SVG). Defaults to a muted tire-ring
 *               illustration so every call site doesn't need its own.
 *   - title:    string or node. Required for a meaningful empty state.
 *   - lead:     string or node. Optional supporting paragraph.
 *   - action:   { label, href? , onClick?, variant? } — renders a single
 *               button/link below the lead. `variant: "primary"` (default)
 *               or `"ghost"`. Pass an `href` for links, `onClick` for
 *               buttons.
 *   - children: optional additional content below the action (e.g. a
 *               secondary helper line).
 */

/* Lucide `inbox` — universal "nothing here" glyph. Replaces the
 * previous concentric-circles icon which read as a swim ring rather
 * than a tire. Deliberately not domain-specific (not a wheel / tire):
 * this component is used by "no favorites", "no saved searches",
 * "no results" etc. — all contexts want the same calm empty vibe. */
const DefaultIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
    <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
  </svg>
);

const EmptyState = ({ icon, title, lead, action, children, className }) => {
  const classes = [css.root, className].filter(Boolean).join(' ');

  const renderAction = () => {
    if (!action) return null;
    const { label, href, onClick, variant = 'primary' } = action;
    const actionClass = variant === 'ghost' ? css.actionGhost : css.actionPrimary;
    if (href) {
      return (
        <a href={href} className={actionClass}>
          {label}
        </a>
      );
    }
    return (
      <button type="button" className={actionClass} onClick={onClick}>
        {label}
      </button>
    );
  };

  return (
    <div className={classes}>
      <div className={css.illo}>{icon ?? <DefaultIcon />}</div>
      {title ? <p className={css.title}>{title}</p> : null}
      {lead ? <p className={css.lead}>{lead}</p> : null}
      {renderAction()}
      {children}
    </div>
  );
};

export default EmptyState;
